import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/business/components/PageHeader";
import { ComingSoonBadge } from "@/business/components/ComingSoonBadge";

const SAMPLE_THREADS = [
  { id: "t1", name: "BuskerBeat Team", preview: "Welcome aboard! Here’s how to get the most out of your venue page …", unread: 1, time: "Just now",
    messages: [
      { from: "them", text: "Welcome to BuskerBeat for Business! Reply here if you need help setting up.", time: "Just now" },
    ]
  },
  { id: "t2", name: "Daniel Park", preview: "Hi! I’d love to perform at your venue …", unread: 0, time: "Yesterday",
    messages: [
      { from: "them", text: "Hi! I’d love to perform at your venue. Are you open to acoustic acts on weekends?", time: "Yesterday · 6:14 PM" },
    ]
  },
];

export default function MessagesPage() {
  const [threads, setThreads] = useState(SAMPLE_THREADS);
  const [activeId, setActiveId] = useState(SAMPLE_THREADS[0]?.id);
  const [draft, setDraft] = useState("");

  const active = threads.find((t) => t.id === activeId);

  const openThread = (id) => {
    setActiveId(id);
    setThreads((s) => s.map((t) => (t.id === id ? { ...t, unread: 0 } : t)));
  };
  const send = (e) => {
    e.preventDefault();
    if (!draft.trim() || !active) return;
    setThreads((s) => s.map((t) => (t.id === active.id ? { ...t, messages: [...t.messages, { from: "me", text: draft.trim(), time: "Now" }] } : t)));
    setDraft("");
    toast("Sent (preview only — real-time messaging coming soon).");
  };

  return (
    <div className="space-y-6" data-testid="messages-page">
      <PageHeader title="Messages" description="Talk to buskers and customers." action={<ComingSoonBadge label="Real-time soon" />} />
      <Card className="border-[#E5E7EB] shadow-sm overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] min-h-[520px]">
          {/* Threads list */}
          <aside className="border-b md:border-b-0 md:border-r border-[#E5E7EB] bg-white" data-testid="messages-thread-list">
            <div className="p-3 border-b border-[#E5E7EB]"><Input placeholder="Search conversations" data-testid="messages-search-input" /></div>
            {threads.length === 0 ? (
              <div className="py-10 text-center" data-testid="messages-empty-state">
                <div className="mx-auto h-12 w-12 rounded-2xl bg-[#F3F4F6] text-[#6B7280] flex items-center justify-center"><MessageSquare className="h-6 w-6" /></div>
                <p className="mt-3 text-sm font-medium">No conversations</p>
              </div>
            ) : (
              <ul>
                {threads.map((t) => (
                  <li key={t.id}>
                    <button onClick={() => openThread(t.id)} className={`w-full text-left px-4 py-3 border-b border-[#E5E7EB] flex items-start gap-3 transition-colors duration-150 ${t.id === activeId ? "bg-[#FFF8F1]" : "hover:bg-[#F9FAFB]"}`} data-testid={`messages-thread-row-${t.id}`}>
                      <span className="inline-flex h-9 w-9 rounded-full bg-[#FFF1E8] text-[#FF6A00] items-center justify-center text-sm font-semibold flex-shrink-0">{t.name.slice(0,1)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2"><span className="text-sm font-semibold truncate">{t.name}</span><span className="text-[11px] text-[#6B7280] flex-shrink-0">{t.time}</span></span>
                        <span className="flex items-center justify-between gap-2 mt-0.5"><span className="text-xs text-[#6B7280] truncate">{t.preview}</span>{t.unread > 0 && <Badge className="bg-[#FF6A00] text-white hover:bg-[#FF6A00]" data-testid={`messages-unread-badge-${t.id}`}>{t.unread}</Badge>}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
          {/* Thread */}
          <section className="flex flex-col bg-[#FAFAFB]" data-testid="messages-thread-pane">
            {active ? (
              <>
                <header className="px-4 py-3 border-b border-[#E5E7EB] bg-white flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 rounded-full bg-[#FFF1E8] text-[#FF6A00] items-center justify-center text-sm font-semibold">{active.name.slice(0,1)}</span>
                  <div className="min-w-0"><div className="text-sm font-semibold">{active.name}</div><div className="text-[11px] text-[#6B7280]">Conversation</div></div>
                </header>
                <CardContent className="flex-1 p-4 space-y-3 overflow-y-auto">
                  {active.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${m.from === "me" ? "bg-[#FF6A00] text-white rounded-br-sm" : "bg-white border border-[#E5E7EB] text-[#111827] rounded-bl-sm"}`}>
                        <div>{m.text}</div>
                        <div className={`text-[10px] mt-1 ${m.from === "me" ? "text-white/80" : "text-[#6B7280]"}`}>{m.time}</div>
                      </div>
                    </div>
                  ))}
                </CardContent>
                <form onSubmit={send} className="p-3 border-t border-[#E5E7EB] bg-white flex items-center gap-2" data-testid="messages-compose-form">
                  <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Write a message …" data-testid="messages-compose-input" />
                  <Button type="submit" className="bg-[#FF6A00] hover:bg-[#E85F00] text-white" data-testid="messages-send-button"><Send className="h-4 w-4" /></Button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-[#6B7280]">Select a conversation to start chatting.</div>
            )}
          </section>
        </div>
      </Card>
    </div>
  );
}
